import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { bookingApi } from '../../services/bookingService';
import { formatBookingDateTimeRange, getCalendarProviderLabel, getBookingStatusLabel } from '../../utils/bookings';
import { resolveApiUrl } from '../../services/api';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import type { BookingCreateResponse } from '../../types';
import './MyBookings.css';

const getServiceImage = (serviceId: string): string => {
  switch (serviceId) {
    case 'ceiling-solution':
      return '/images/offer-ceiling.jpg';
    case 'workspace-solution':
      return '/images/offer-workspace.jpg';
    case 'education-furniture':
      return '/images/offer-education.jpg';
    case 'hospitality-furniture':
      return '/images/offer-hotel.jpg';
    default:
      return '/images/hero-blueprint.jpg';
  }
};

function MyBookings() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [bookings, setBookings] = useState<BookingCreateResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'ALL' | 'REQUESTED' | 'CONFIRMED' | 'CANCELLED'>('ALL');

  useEffect(() => {
    let mounted = true;
    if (!user) return;

    setLoading(true);
    bookingApi.getMyBookings()
      .then((data) => {
        if (!mounted) return;
        setBookings(data);
        setError(null);
      })
      .catch((err) => {
        if (!mounted) return;
        console.error('Error fetching bookings:', err);
        setError(t('errors.connectionTitle'));
        toast.error(t('booking.slotsLoadFailed'));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [user, t]);

  const isRtl = i18n.resolvedLanguage?.startsWith('ar') ?? false;

  // Stats calculation
  const totalCount = bookings.length;
  const pendingCount = bookings.filter(b => b.status === 'REQUESTED').length;
  const confirmedCount = bookings.filter(b => b.status === 'CONFIRMED').length;
  const cancelledCount = bookings.filter(b => b.status === 'CANCELLED').length;

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'ALL') return true;
    return booking.status === filter;
  });

  const SkeletonLoader = () => (
    <div className="bookings-skeleton-container">
      {[1, 2, 3].map((n) => (
        <div key={n} className="booking-skeleton-card">
          <div className="skeleton-image shimmer"></div>
          <div className="skeleton-content">
            <div className="skeleton-header">
              <div className="skeleton-badge shimmer"></div>
              <div className="skeleton-title shimmer"></div>
            </div>
            <div className="skeleton-grid">
              <div className="skeleton-grid-item shimmer"></div>
              <div className="skeleton-grid-item shimmer"></div>
              <div className="skeleton-grid-item shimmer"></div>
              <div className="skeleton-grid-item shimmer"></div>
            </div>
            <div className="skeleton-footer">
              <div className="skeleton-btn shimmer"></div>
              <div className="skeleton-calendar-dl shimmer"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <main className={`page-main bookings-page ${isRtl ? 'rtl' : 'ltr'}`}>
      <div className="bookings-container container">
        <header className="bookings-header reveal visible">
          <h1>{t('myBookings.title')}</h1>
          <p>{t('myBookings.subtitle')}</p>
        </header>

        {loading ? (
          <>
            {/* Skeleton Stats */}
            <div className="bookings-stats-grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="stat-card shimmer" style={{ height: '110px' }}></div>
              ))}
            </div>
            <SkeletonLoader />
          </>
        ) : error ? (
          <div className="bookings-error-state">
            <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <h3>{error}</h3>
            <p>{t('errors.connectionDesc')}</p>
            <button className="btn btn-filled" onClick={() => window.location.reload()}>
              {t('errors.retry')}
            </button>
          </div>
        ) : (
          <>
            {/* Stats Dashboard */}
            <div className="bookings-stats-grid reveal visible">
              <div className="stat-card total" onClick={() => setFilter('ALL')}>
                <div className="stat-icon-wrapper">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <div className="stat-details">
                  <h3>{totalCount}</h3>
                  <p>{t('myBookings.statTotal')}</p>
                </div>
              </div>

              <div className="stat-card pending" onClick={() => setFilter('REQUESTED')}>
                <div className="stat-icon-wrapper">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                </div>
                <div className="stat-details">
                  <h3>{pendingCount}</h3>
                  <p>{t('myBookings.statPending')}</p>
                </div>
              </div>

              <div className="stat-card confirmed" onClick={() => setFilter('CONFIRMED')}>
                <div className="stat-icon-wrapper">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                </div>
                <div className="stat-details">
                  <h3>{confirmedCount}</h3>
                  <p>{t('myBookings.statConfirmed')}</p>
                </div>
              </div>

              <div className="stat-card cancelled" onClick={() => setFilter('CANCELLED')}>
                <div className="stat-icon-wrapper">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                </div>
                <div className="stat-details">
                  <h3>{cancelledCount}</h3>
                  <p>{t('myBookings.statCancelled')}</p>
                </div>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="bookings-filter-bar reveal visible">
              <button 
                className={`filter-tab ${filter === 'ALL' ? 'active' : ''}`}
                onClick={() => setFilter('ALL')}
              >
                <span>{t('myBookings.filterAll')}</span>
                <span className="filter-count">{totalCount}</span>
              </button>
              <button 
                className={`filter-tab ${filter === 'REQUESTED' ? 'active' : ''}`}
                onClick={() => setFilter('REQUESTED')}
              >
                <span className="dot dot-requested"></span>
                <span>{t('myBookings.filterPending')}</span>
                <span className="filter-count">{pendingCount}</span>
              </button>
              <button 
                className={`filter-tab ${filter === 'CONFIRMED' ? 'active' : ''}`}
                onClick={() => setFilter('CONFIRMED')}
              >
                <span className="dot dot-confirmed"></span>
                <span>{t('myBookings.filterConfirmed')}</span>
                <span className="filter-count">{confirmedCount}</span>
              </button>
              <button 
                className={`filter-tab ${filter === 'CANCELLED' ? 'active' : ''}`}
                onClick={() => setFilter('CANCELLED')}
              >
                <span className="dot dot-cancelled"></span>
                <span>{t('myBookings.filterCancelled')}</span>
                <span className="filter-count">{cancelledCount}</span>
              </button>
            </div>

            {/* Bookings View */}
            {filteredBookings.length === 0 ? (
              <div className="bookings-empty-state reveal visible">
                <div className="empty-icon-container">
                  <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </div>
                <h3>{t('myBookings.noBookings')}</h3>
                <p>{t('myBookings.noBookingsDesc')}</p>
                <Link to="/consultation" className="btn btn-filled">
                  {t('myBookings.bookBtn')}
                </Link>
              </div>
            ) : (
              <div className="bookings-list">
                {filteredBookings.map((booking, index) => {
                  const serviceImg = getServiceImage(booking.serviceId);
                  const statusClass = `status-${booking.status.toLowerCase()}`;
                  
                  return (
                    <div key={booking.id} className="booking-card reveal visible" style={{ animationDelay: `${index * 0.08}s` }}>
                      <div className="booking-card-image">
                        <img src={serviceImg} alt={booking.serviceName} />
                        <div className="booking-card-overlay"></div>
                      </div>
                      <div className="booking-card-content">
                        <div className="booking-card-header">
                          <div>
                            <span className={`booking-status-badge ${statusClass}`}>
                              <span className="badge-pulse-dot"></span>
                              {getBookingStatusLabel(t, booking.status)}
                            </span>
                            <h2 className="booking-service-title">{booking.serviceName}</h2>
                          </div>
                          <span className="booking-created-date">
                            {t('myBookings.created')}: {new Date(booking.createdAt || '').toLocaleDateString(isRtl ? 'ar-SA' : 'en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>

                        <div className="booking-details-grid">
                          <div className="booking-detail-item">
                            <div className="detail-icon-wrapper">
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                              </svg>
                            </div>
                            <div>
                              <span>{t('booking.schedule')}</span>
                              <strong>{formatBookingDateTimeRange(booking.startAt, booking.endAt, i18n.resolvedLanguage)}</strong>
                            </div>
                          </div>

                          <div className="booking-detail-item">
                            <div className="detail-icon-wrapper">
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/><line x1="12" y1="12" x2="12" y2="12.01"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                              </svg>
                            </div>
                            <div>
                              <span>{t('booking.timezone')}</span>
                              <strong>{booking.timezone}</strong>
                            </div>
                          </div>

                          <div className="booking-detail-item">
                            <div className="detail-icon-wrapper">
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                              </svg>
                            </div>
                            <div>
                              <span>{t('booking.form.email')}</span>
                              <strong>{booking.email}</strong>
                            </div>
                          </div>

                          <div className="booking-detail-item">
                            <div className="detail-icon-wrapper">
                              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.5 19.5 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                              </svg>
                            </div>
                            <div>
                              <span>{t('booking.form.phone')}</span>
                              <strong>{booking.phone}</strong>
                            </div>
                          </div>

                          {booking.company && (
                            <div className="booking-detail-item">
                              <div className="detail-icon-wrapper">
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                                </svg>
                              </div>
                              <div>
                                <span>{t('booking.form.company')}</span>
                                <strong>{booking.company}</strong>
                              </div>
                            </div>
                          )}
                        </div>

                        {booking.notes && (
                          <div className="booking-notes-section">
                            <span>{t('myBookings.notes')}</span>
                            <p>{booking.notes}</p>
                          </div>
                        )}

                        <div className="booking-card-footer">
                          <div className="booking-meeting-section">
                            {booking.status === 'CONFIRMED' ? (
                              booking.meetingLink ? (
                                <a
                                  href={booking.meetingLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-filled join-meeting-btn"
                                >
                                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: isRtl ? '0' : '8px', marginLeft: isRtl ? '8px' : '0' }}>
                                    <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                                  </svg>
                                  <span>{t('myBookings.joinMeeting')}</span>
                                </a>
                              ) : (
                                <span className="meeting-link-placeholder confirmed">{t('myBookings.meetingAvailable')}: N/A</span>
                              )
                            ) : booking.status === 'REQUESTED' ? (
                              <div className="meeting-waiting-banner">
                                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 15 14"/>
                                </svg>
                                <span>{t('myBookings.meetingNotReady')}</span>
                              </div>
                            ) : (
                              <span className="meeting-link-placeholder">{t('booking.status.cancelled')}</span>
                            )}
                          </div>

                          {booking.calendarOptions && booking.calendarOptions.length > 0 && booking.status !== 'CANCELLED' && (
                            <div className="booking-calendar-downloads">
                              {booking.calendarOptions.map((opt) => (
                                <a
                                  key={opt.provider}
                                  href={resolveApiUrl(opt.url)}
                                  target={opt.download ? undefined : '_blank'}
                                  rel={opt.download ? undefined : 'noreferrer'}
                                  download={opt.download ? `fcs-booking-${booking.id}.ics` : undefined}
                                  className={`calendar-dl-link ${opt.primary ? 'primary' : ''}`}
                                  title={t(opt.primary ? 'booking.calendarPrimaryAction' : 'booking.calendarSecondaryAction', { provider: getCalendarProviderLabel(t, opt.provider) })}
                                >
                                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                                  </svg>
                                  <span>{getCalendarProviderLabel(t, opt.provider)}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}

export default MyBookings;
